import type { StringKey } from '../strings';

/**
 * The help topics (piece 5, decision 0443). A screen opts in with one topic
 * key and draws no explanation of its own, so the same concept reads the same
 * wherever it is explained.
 *
 * 🔨 A topic holds string keys and never text. Every word a person reads comes
 * through the one lookup (decision 0013), and this registry is what stops a
 * definition being retyped on each screen that needs it.
 *
 * 🔨 A topic explains and never acts. It carries no field, no write and no
 * consequence, which is why a comparison table inside one does not make it the
 * page decision 0409 would ask for.
 */

export const HELP_TOPICS = [
  'accountingPeriod', 'chartOfAccounts', 'printColumnSet', 'salesTaxSettings', 'writeOffVsVoid', 'dealBudget', 'dealAuthority', 'dealNeed', 'dealTiming', 'dealValue',
  'importBlocks', 'importRead', 'importScopes', 'importDateRange', 'importStepped', 'importCancel', 'importResults',
  'printGuideTemplates', 'printGuideDefault', 'printGuideRegions', 'printGuidePageFont', 'printGuideLabels', 'printGuideOverlays', 'printGuideZoom', 'printGuideSave', 'printGuidePreview',
  'approvalTiers', 'importMappings', 'emailTemplates',
] as const;
export type HelpTopic = (typeof HELP_TOPICS)[number];

/** A comparison of two named things, one row per question asked of both. */
export interface HelpTable {
  /** The column headings, the first of which names the question column. */
  columns: readonly StringKey[];
  /** Each row is one cell per column, in the same order. */
  rows: readonly (readonly StringKey[])[];
}

export interface HelpTopicContent {
  title: StringKey;
  /** What the concept is, read before the table. */
  lead: readonly StringKey[];
  table?: HelpTable;
  /** The qualifications, read after the table. */
  notes: readonly StringKey[];
}

export const HELP_TOPIC_CONTENT: Readonly<Record<HelpTopic, HelpTopicContent>> = {
  accountingPeriod: {
    title: 'help.accountingPeriod.title',
    lead: ['help.accountingPeriod.lead1', 'help.accountingPeriod.lead2'],
    notes: ['help.accountingPeriod.note1', 'help.accountingPeriod.note2', 'help.accountingPeriod.note3'],
  },
  chartOfAccounts: {
    title: 'help.chartOfAccounts.title',
    lead: ['help.chartOfAccounts.lead1', 'help.chartOfAccounts.lead2'],
    notes: ['help.chartOfAccounts.note1', 'help.chartOfAccounts.note2'],
  },
  /**
   * 🔒 T194 (decision 0657): what a column set is. 🔨 Its table is computed by the
   * page that opens it, from the sets and the column headings the shared package
   * already holds, so the list cannot come to disagree with what the sheet
   * draws; a table of typed keys would be a second copy of it.
   */
  printColumnSet: {
    title: 'help.printColumnSet.title',
    lead: ['help.printColumnSet.lead1'],
    notes: ['help.printColumnSet.note1', 'help.printColumnSet.note2'],
  },
  /**
   * 🔒 T230 (decision 0708): what the Sales tax page's sections mean — the dated
   * series, the local range, the source and the seeded mark, the rules and the
   * nexus warning — so each section keeps one line of its own and the rest is
   * read here, transcribed from piece 25.
   */
  salesTaxSettings: {
    title: 'help.salesTaxSettings.title',
    lead: ['help.salesTaxSettings.lead1', 'help.salesTaxSettings.lead2'],
    notes: ['help.salesTaxSettings.note1', 'help.salesTaxSettings.note2', 'help.salesTaxSettings.note3', 'help.salesTaxSettings.note4'],
  },
  /**
   * 🔒 T252 (decision 0724): one topic per row of the Deal's Intent card — what the
   * row means, what completes it, and what Deal Progress and Next Action read from
   * it — transcribed from piece 19. The card's own head opens the four together.
   */
  dealBudget: {
    title: 'help.dealBudget.title',
    lead: ['help.dealBudget.lead1'],
    notes: ['help.dealBudget.note1', 'help.dealBudget.note2', 'help.dealBudget.note3'],
  },
  dealAuthority: {
    title: 'help.dealAuthority.title',
    lead: ['help.dealAuthority.lead1'],
    notes: ['help.dealAuthority.note1', 'help.dealAuthority.note2', 'help.dealAuthority.note3'],
  },
  dealNeed: {
    title: 'help.dealNeed.title',
    lead: ['help.dealNeed.lead1'],
    notes: ['help.dealNeed.note1', 'help.dealNeed.note2'],
  },
  dealTiming: {
    title: 'help.dealTiming.title',
    lead: ['help.dealTiming.lead1'],
    notes: ['help.dealTiming.note1', 'help.dealTiming.note2'],
  },
  /**
   * 🔒 T426 (decision 1219): where the Deal value comes from, opened by the ? beside
   * the figure. The one line naming this Deal's own source is the page's, drawn
   * between the order and the note, so the topic itself stays the same everywhere.
   */
  dealValue: {
    title: 'help.dealValue.title',
    lead: ['help.dealValue.lead1', 'help.dealValue.lead2', 'help.dealValue.lead3', 'help.dealValue.lead4', 'help.dealValue.lead5'],
    notes: ['help.dealValue.note1'],
  },
  writeOffVsVoid: {
    title: 'help.writeOffVsVoid.title',
    lead: ['help.writeOffVsVoid.lead1'],
    table: {
      columns: ['help.writeOffVsVoid.column.question', 'help.writeOffVsVoid.column.writeOff', 'help.writeOffVsVoid.column.void'],
      rows: [
        ['help.writeOffVsVoid.row.when', 'help.writeOffVsVoid.when.writeOff', 'help.writeOffVsVoid.when.void'],
        ['help.writeOffVsVoid.row.posts', 'help.writeOffVsVoid.posts.writeOff', 'help.writeOffVsVoid.posts.void'],
        ['help.writeOffVsVoid.row.invoice', 'help.writeOffVsVoid.invoice.writeOff', 'help.writeOffVsVoid.invoice.void'],
        ['help.writeOffVsVoid.row.cash', 'help.writeOffVsVoid.cash.writeOff', 'help.writeOffVsVoid.cash.void'],
        ['help.writeOffVsVoid.row.undo', 'help.writeOffVsVoid.undo.writeOff', 'help.writeOffVsVoid.undo.void'],
      ],
    },
    notes: ['help.writeOffVsVoid.note1', 'help.writeOffVsVoid.note2', 'help.writeOffVsVoid.note3'],
  },
  /**
   * 🔒 T435 (decision 1103): the Import page's guide to the Zoho Books pull,
   * seven topics transcribed from piece 31's *The help window*, which is
   * written first; `topics.test.ts` fails on any word the piece does not carry.
   * 🔨 Every line is a lead, drawn at the reading size: a guide's lines are
   * its content, and a note's small grey type is for a qualification.
   */
  importBlocks: {
    title: 'help.importBlocks.title',
    lead: ['help.importBlocks.lead1', 'help.importBlocks.lead2', 'help.importBlocks.lead3', 'help.importBlocks.lead4', 'help.importBlocks.lead5', 'help.importBlocks.lead6'],
    notes: [],
  },
  importRead: {
    title: 'help.importRead.title',
    lead: ['help.importRead.lead1', 'help.importRead.lead2', 'help.importRead.lead3', 'help.importRead.lead4', 'help.importRead.lead5', 'help.importRead.lead6'],
    notes: [],
  },
  importScopes: {
    title: 'help.importScopes.title',
    lead: ['help.importScopes.lead1', 'help.importScopes.lead2', 'help.importScopes.lead3', 'help.importScopes.lead4', 'help.importScopes.lead5'],
    notes: [],
  },
  importDateRange: {
    title: 'help.importDateRange.title',
    lead: ['help.importDateRange.lead1', 'help.importDateRange.lead2', 'help.importDateRange.lead3', 'help.importDateRange.lead4', 'help.importDateRange.lead5'],
    notes: [],
  },
  importStepped: {
    title: 'help.importStepped.title',
    lead: ['help.importStepped.lead1', 'help.importStepped.lead2', 'help.importStepped.lead3', 'help.importStepped.lead4', 'help.importStepped.lead5', 'help.importStepped.lead6'],
    notes: [],
  },
  importCancel: {
    title: 'help.importCancel.title',
    lead: ['help.importCancel.lead1', 'help.importCancel.lead2', 'help.importCancel.lead3'],
    notes: [],
  },
  importResults: {
    title: 'help.importResults.title',
    lead: ['help.importResults.lead1', 'help.importResults.lead2', 'help.importResults.lead3', 'help.importResults.lead4', 'help.importResults.lead5', 'help.importResults.lead6'],
    notes: [],
  },
  /**
   * 🔒 T557 (decision 1282): the print templates pages' About This Page window,
   * nine topics transcribed from piece 27's *The About This Page window*, which
   * is written first; `topics.test.ts` fails on any word the piece does not
   * carry. Every line is a lead, as the Import guide's are (decision 1103).
   */
  printGuideTemplates: {
    title: 'help.printGuideTemplates.title',
    lead: ['help.printGuideTemplates.lead1', 'help.printGuideTemplates.lead2', 'help.printGuideTemplates.lead3', 'help.printGuideTemplates.lead4'],
    notes: [],
  },
  printGuideDefault: {
    title: 'help.printGuideDefault.title',
    lead: ['help.printGuideDefault.lead1', 'help.printGuideDefault.lead2', 'help.printGuideDefault.lead3', 'help.printGuideDefault.lead4'],
    notes: [],
  },
  printGuideRegions: {
    title: 'help.printGuideRegions.title',
    lead: ['help.printGuideRegions.lead1', 'help.printGuideRegions.lead2', 'help.printGuideRegions.lead3', 'help.printGuideRegions.lead4'],
    notes: [],
  },
  printGuidePageFont: {
    title: 'help.printGuidePageFont.title',
    lead: ['help.printGuidePageFont.lead1', 'help.printGuidePageFont.lead2', 'help.printGuidePageFont.lead3'],
    notes: [],
  },
  printGuideLabels: {
    title: 'help.printGuideLabels.title',
    lead: ['help.printGuideLabels.lead1', 'help.printGuideLabels.lead2'],
    notes: [],
  },
  printGuideOverlays: {
    title: 'help.printGuideOverlays.title',
    lead: ['help.printGuideOverlays.lead1', 'help.printGuideOverlays.lead2', 'help.printGuideOverlays.lead3'],
    notes: [],
  },
  printGuideZoom: {
    title: 'help.printGuideZoom.title',
    lead: ['help.printGuideZoom.lead1', 'help.printGuideZoom.lead2', 'help.printGuideZoom.lead3', 'help.printGuideZoom.lead4'],
    notes: [],
  },
  printGuideSave: {
    title: 'help.printGuideSave.title',
    lead: ['help.printGuideSave.lead1', 'help.printGuideSave.lead2', 'help.printGuideSave.lead3'],
    notes: [],
  },
  printGuidePreview: {
    title: 'help.printGuidePreview.title',
    lead: ['help.printGuidePreview.lead1', 'help.printGuidePreview.lead2', 'help.printGuidePreview.lead3', 'help.printGuidePreview.lead4'],
    notes: [],
  },
  /**
   * 🔒 T1085 (decision 1841): the three configuration pages set once and edited
   * rarely, each one concept window beside its title and no stepper. Every line
   * is a lead, transcribed from its owning piece's passage, and `topics.test.ts`
   * fails on any word the piece does not carry.
   */
  approvalTiers: {
    title: 'help.approvalTiers.title',
    lead: ['help.approvalTiers.lead1', 'help.approvalTiers.lead2', 'help.approvalTiers.lead3', 'help.approvalTiers.lead4', 'help.approvalTiers.lead5', 'help.approvalTiers.lead6'],
    notes: [],
  },
  importMappings: {
    title: 'help.importMappings.title',
    lead: ['help.importMappings.lead1', 'help.importMappings.lead2', 'help.importMappings.lead3', 'help.importMappings.lead4', 'help.importMappings.lead5', 'help.importMappings.lead6', 'help.importMappings.lead8', 'help.importMappings.lead7'],
    notes: [],
  },
  emailTemplates: {
    title: 'help.emailTemplates.title',
    lead: ['help.emailTemplates.lead1', 'help.emailTemplates.lead2', 'help.emailTemplates.lead3', 'help.emailTemplates.lead4', 'help.emailTemplates.lead5'],
    notes: [],
  },
};

/** 🔒 T1085 (decision 1841): each concept window, the piece it is held to and the passage's own heading there. */
export const CONCEPT_TOPICS = [
  { topic: 'approvalTiers', piece: '22-accounting-documents-and-the-approval-gate.md', heading: '**The Approval Tiers window**' },
  { topic: 'importMappings', piece: '31-first-cutover-and-the-module-releases.md', heading: '**The Import Mappings window**' },
  { topic: 'emailTemplates', piece: '09-files-versions-sharing-audit-and-outbound-mail-w.md', heading: '**The Email Templates window**' },
] as const satisfies readonly { topic: HelpTopic; piece: string; heading: string }[];

/** 🔒 The Import page's help window reads these seven together, in this order (T435; decision 1103). */
export const IMPORT_GUIDE_TOPICS = ['importBlocks', 'importRead', 'importScopes', 'importDateRange', 'importStepped', 'importCancel', 'importResults'] as const satisfies readonly HelpTopic[];

/** 🔒 The print templates pages' About This Page window reads these nine together, in this order (T557; decision 1282). */
export const PRINT_GUIDE_TOPICS = ['printGuideTemplates', 'printGuideDefault', 'printGuideRegions', 'printGuidePageFont', 'printGuideLabels', 'printGuideOverlays', 'printGuideZoom', 'printGuideSave', 'printGuidePreview'] as const satisfies readonly HelpTopic[];

/** The content for a topic, for a renderer that has only the key. */
export function helpTopicContent(topic: HelpTopic): HelpTopicContent {
  return HELP_TOPIC_CONTENT[topic];
}

/** Every string key a topic reads, in the order it is drawn. */
export function helpTopicKeys(topic: HelpTopic): readonly StringKey[] {
  const content = HELP_TOPIC_CONTENT[topic];
  const table = content.table ? [...content.table.columns, ...content.table.rows.flat()] : [];
  return [content.title, ...content.lead, ...table, ...content.notes];
}
